import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

import { Injectable } from "@angular/core";
import { forkJoin, Observable, of, Subject, throwError, firstValueFrom } from "rxjs";
import { map, switchMap, filter, tap, retry, catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { environment } from "../../environments/environment";

import { Note } from "../models/site-review/Note";
import { Report } from "../models/site-review/Report";
import { ReportItem } from "../models/site-review/ReportItem";
import { JobInfraction} from "../models/site-review/job-infraction";
import { Question } from "../models/site-review/Question";
import { Polygon } from "app/models/site-review/Polygon";
import { Tract } from "app/models/site-review/Tract";
import { Assignment } from "app/models/site-review/Assignment";
import { AssignmentTeam } from "app/views/admin/teams-list/teams-list.component";
import { AssignmentSchedule } from "app/models/site-review/AssignmentSchedule";
import { Command } from "app/models/command";
import { TableMap } from './table-map';
import { Site } from 'app/models/site-review/Site';
import { GoogleFolderCommandSet } from 'app/models/google-folder-command-set';
import { IdbService } from './idb.service';
import { CalendarEvent } from 'app/models/calendar-event';
import { ToolboxService } from 'app/toolbox.service';
import { JobEventListItem } from 'app/models/site-review/job-event-list-item';
import { ReportTemplate } from 'app/models/site-review/ReportTemplate';
import { User } from 'app/models/User';
import { Office } from 'app/models/office';

export const apiServer = environment.siteReviewURL;
export const pdfServer = environment.serverURL;
export const adobePDFkey = environment.adobePDFkey;

@Injectable()
export class SiteReviewService {
  public sessionId: string = "";
  public apiServer: string = "";
  public pdfServer: string = "";
  private sessionIdKey = 'sr_session_id';
  public selectedReport: Report;
  public tableMap: TableMap;
  private eventBrocker = new Subject<AppEvent<any>>();
  public adobePDFkey = "";

  private supervisorsListSubject: Subject<User[]> = new Subject<User[]>();
  public supervisors$ = this.supervisorsListSubject.asObservable();  //these are all supervisors at a crew level:  FM, GF and SUP..   property crewLevel tells them apart.
  //public googleAPIKey: string = "";
  public googleAPIKey: string = environment.googleAPIKey;

  public googleWorkingFolders: [] = [];

  credentials = {
    username: '',
    password: ''
  };

  private locateTicketInput$ = new Subject<string>();


  constructor(private _http: HttpClient, private idb: IdbService, private toolbox: ToolboxService) {
    this.sessionId = localStorage.getItem(this.sessionIdKey);
    this.apiServer = apiServer;
    this.pdfServer = pdfServer;
    this.tableMap = new TableMap();
    this.adobePDFkey = adobePDFkey;
  }

  loadSupervisors(): Observable<any> {

    let cmd = new Command();
    cmd.procedure = 'cmdGetSupervisorOptions';
    let url: string = this.apiServer + '/api/cmd?sid=' + this.sessionId + '&target=cmdGetSupervisorOptions';
    return this._http.post(url, cmd).pipe(

      tap(obj => {
        this.supervisorsListSubject.next(obj);
        //console.log('getSupervisors', obj)
      })
    );

  }

  getFinancialReviewData(startDate, endDate) {
    let url: string = `${this.apiServer}/api/timesheet/admin/review?sid=${this.sessionId}&start=${startDate}&end=${endDate}`;
    return this._http.get(url)
      .pipe(map((response: any) => <any>response));
  }

  updateTSReviewStatus(timeSheetID, status, comments?) {
    if (!comments) comments = '';
    return this._http
      .put(
        `${this.apiServer}/api/penta/timesheet/admin/status/${timeSheetID}?sid=${this.sessionId}`,
        { Status: status, comments: comments },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .pipe(
        catchError(error => {
          console.error(`Failed to update timeSheetID ${timeSheetID}:`, error);
          return of(null);
        })
      )
  }

  getOffice(officeId: number) {
    var obj = new Command();
    obj.addParameter("OfficeID", officeId);
    obj.procedure = "cmdOffice";
    return this.command(obj)
  }
  updateOffice(o: Office) {

    if (!o.officeID) o.officeID = 0;
    var obj = new Command();
    obj.addParameter("OfficeID", o.officeID);
    obj.addParameter("OfficeName", o.officeName);
    obj.addParameter("Address1", o.address1);
    obj.addParameter("City", o.city);
    obj.addParameter("State", o.state);
    obj.addParameter("PostalCode", o.postalCode);
    obj.addParameter("Phone", o.phone);
    obj.addParameter("OfficeCode", o.officeCode);
    obj.addParameter("OfficeNumber", o.officeNumber);
    obj.addParameter("OfficeStatus", o.officeStatus);
    obj.addParameter("DistrictID", o.districtID);
    obj.procedure = "cmdOfficeUpdate";


    return this.command(obj)
  }
  updateTSStatusAndComments(timeSheetIDs: number | number[], status: string, comments?: string): Observable<any> {
    const idsArray = Array.isArray(timeSheetIDs) ? timeSheetIDs : [timeSheetIDs];

    const payload: any = { Status: status };
    if (comments !== undefined) {
      payload.Comment = comments;
    }

    const seenIDs = new Set<number>();
    const uniqueTimeSheetIDs = idsArray.filter(id => {
      if (seenIDs.has(id)) return false;
      seenIDs.add(id);
      return true;
    });

    return forkJoin(
      uniqueTimeSheetIDs.map(timeSheetID =>
        this._http
          .put(
            `${this.apiServer}/api/timesheet/admin/status/${timeSheetID}?sid=${this.sessionId}`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
          )
          .pipe(
            catchError(error => {
              console.error(`Failed to update timeSheetID ${timeSheetID}:`, error);
              return of(null);
            })
          )
      )
    );
  }

  updatePAStatusAndComments(
    timeSheetIDs: number | number[], 
    status?: string, 
    comments?: string, 
    refType?: string, 
    refID?: number
  ): Observable<any> {
  
    const idsArray = Array.isArray(timeSheetIDs) ? timeSheetIDs : [timeSheetIDs];
  
    const payload: any = {};
  
    // Mejores validaciones
    if (status != null && status !== '') {
      payload.status = status;
    }
  
    if (comments != null && comments !== '') {
      payload.comment = comments;
    }
  
    if (refType != null && refType !== '') {
      payload.refType = refType;
    }
  
    if (refID != null) {                   
      payload.refID = refID;
    }
  
    const seenIDs = new Set<number>();
    const uniqueTimeSheetIDs = idsArray.filter(id => {
      if (seenIDs.has(id)) return false;
      seenIDs.add(id);
      return true;
    });
  
    return forkJoin(
      uniqueTimeSheetIDs.map(timeSheetID =>
        this._http
          .put(
            `${this.apiServer}/api/timesheet/admin/pa-status/${timeSheetID}?sid=${this.sessionId}`,
            payload,
            { headers: { 'Content-Type': 'application/json' } }
          )
          .pipe(
            catchError(error => {
              console.error(`Failed to update timeSheetID ${timeSheetID}:`, error);
              return of(null);
            })
          )
      )
    );
  }

  getPAComments(timeSheetID) {
    let url = `${this.apiServer}/api/timesheet/admin/pa-status/${timeSheetID}/comments?sid=${this.sessionId}`;
    return this._http.get(url).pipe(
      map((response: any) => {
        return Array.isArray(response) && response.length > 0 ? response[0] : response || {};
      })
    );
  }


  exportTimesheetPentaFinancial(uniqueTimeSheetIDs) {

    let url = `${this.apiServer}/api/timesheet/export/async?sid=${this.sessionId}`
    return this._http
      .post(url, uniqueTimeSheetIDs,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .pipe(
        catchError(error => {
          console.error(`Failed to export timeSheets ${uniqueTimeSheetIDs}:`, error);
          return of(null);
        })
      )
  }

  financialEditCrewValues(timeSheetID, crewMember) {
    let url = `${this.apiServer}/api/timesheet/admin/${timeSheetID}/crews/${crewMember.timeSheetCrewID}?sid=${this.sessionId}`;
    return this._http
      .put(url, crewMember,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .pipe(
        catchError(error => {
          console.error(`Failed to update crew in ${crewMember.timeSheetCrewID}:`, error);
          return of(null);
        })
      )
  }
  financialEditEquipmentValues(timeSheetID, equipment) {
    let url = `${this.apiServer}/api/timesheet/admin/${timeSheetID}/equipments/${equipment.timeSheetEquipmentID}?sid=${this.sessionId}`;
    return this._http
      .put(url, equipment,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .pipe(
        catchError(error => {
          console.error(`Failed to update values in  ${equipment.timeSheetEquipmentID}:`, error);
          return of(null);
        })
      )
  }

  tsChangeJobData(timeSheetID, timeSheetJobID, payload) {
    let url = `${this.apiServer}/api/timesheet/admin/${timeSheetID}/jobs/${timeSheetJobID}?sid=${this.sessionId}`;
    return this._http
      .put(url, payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .pipe(
        catchError(error => {
          console.error(`Failed to update values in timeSheetJobID ${timeSheetJobID}:`, error);
          return of(null);
        })
      )
  }

  getPentaCredentials(): { pentaUser: string; pentaPassword: string } | null {
    const pentaUserCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('pentaUser='));

    const pentaPasswordCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('pentaPassword='));

    const pentaRememberMeCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('pentaRememberMe='));

    if (!pentaUserCookie || !pentaPasswordCookie) {
        return null;
    }

    const username = decodeURIComponent(pentaUserCookie.split('=')[1] || '');
    const password = decodeURIComponent(pentaPasswordCookie.split('=')[1] || '');

    if (!username || !password) {
        return null;
    }

    if (pentaRememberMeCookie) {
        const rememberMe = decodeURIComponent(pentaRememberMeCookie.split('=')[1]) === 'true';
        if (rememberMe) {
            return { pentaUser: username, pentaPassword: password };
        }
    }

    return null;
}

    exportToPenta(timesheets) {
        const credentials = this.getPentaCredentials();

        let postTimesheets = {
            "TimeSheets": timesheets,
            "pentaUser": credentials?.pentaUser || '',
            "pentaPassword": credentials?.pentaPassword || ''
        };

        let url: string = `${this.apiServer}/api/penta/timesheet/generate-batch?sid=${this.sessionId}`;
        
        return this._http.post(url, postTimesheets, { withCredentials: true })
            .pipe(map((response: any) => <any>response));
    }


    checkPentaCrendentials(cookiesCredentials) {
      //console.log('cookiescredentials ', cookiesCredentials)
      let url: string = `${this.apiServer}/api/penta/login?sid=${this.sessionId}`;
      return this._http.post(url, cookiesCredentials, { responseType: 'text' });
    }

  // getCredentials() {
  //   const pentaUserCookie = document.cookie
  //     .split('; ')
  //     .find(row => row.startsWith('pentaUser='));
  //   const pentaPasswordCookie = document.cookie
  //     .split('; ')
  //     .find(row => row.startsWith('pentaPassword='));
  //   const pentaRememberMeCookie = document.cookie
  //     .split('; ')
  //     .find(row => row.startsWith('pentaRememberMe='));
  //   this.credentials.username = decodeURIComponent(pentaUserCookie.split('=')[1]);
  //   this.credentials.password = decodeURIComponent(pentaPasswordCookie.split('=')[1]);

  // }


  // exportToPenta(timesheets) {
  //   this.getCredentials();
  //   let postTimesheets = {
  //     "TimeSheets": timesheets,
  //     "pentaUser": this.credentials.username,
  //     "pentaPassword": this.credentials.password
  //   }
  //   let url: string = `${this.apiServer}/api/penta/timesheet/generate-batch?sid=${this.sessionId}`;
  //   return this._http.post(url, postTimesheets, {
  //     withCredentials: true
  //   })
  //     .pipe(map((response: any) => <any>response));
  // }

  getBatchErrorsData(batchID) {
    let url: string = `${this.apiServer}/api/penta/timesheet/errors?batchId=${batchID}&sid=${this.sessionId}`;
    return this._http.get(url)
      .pipe(map((response: any) => <any>response));
  }

  getBatchStatus(batchID) {
    let url: string = `${this.apiServer}/api/penta/timesheet/status?batchId=${batchID}&sid=${this.sessionId}`;
    return this._http.post(url, null)
      .pipe(map((response: any) => <any>response));
  }

  executeBatch(batchID) {
    let url: string = `${this.apiServer}/api/penta/timesheet?batchId=${batchID}&sid=${this.sessionId}`;
    return this._http.post(url, null)
      .pipe(map((response: any) => <any>response));
  }

  tryAgainBatch(batchID) {
    let url: string = `${this.apiServer}/api/penta/timesheet-retry?batchId=${batchID}&sid=${this.sessionId}`;
    return this._http.post(url, batchID)
      .pipe(map((response: any) => <any>response));
  }

  customBatchCsvExport(requestID) {
    let url: string = `${this.apiServer}/api/penta/export-detail/csv?sid=${this.sessionId}&intr_rqst_id=${requestID}`;
    return this._http.get(url, {
      responseType: 'text'
    })
      .pipe(map((response: any) => <any>response));
  }

  resolveTsIssues(timesheet, resolution) {
    let url: string = `${this.apiServer}/api/penta/timesheet/admin/review/${timesheet.timeSheetID}?sid=${this.sessionId}`;
    return this._http.post(url, { status: timesheet.status, comment: resolution },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .pipe(
        catchError(error => {
          console.error(`Failed to resolve timeSheetID ${timesheet.timeSheetID}:`, error);
          return of(null);
        })
      )
      .pipe(map((response: any) => <any>response));
  }

  // DEPRECATED

  // spUpdatePentaBatchStatus(batchID) {
  //   let cmd = new Command();
  //   cmd.procedure = 'spUpdatePentaBatchStatus';
  //   return this.command(cmd).pipe(map(updatedStatus => {
  //     console.log('googleWospUpdatePentaBatchStatusrkingFolders');
  //     return updatedStatus;
  //   }));
  // }

  public getMapLocation(fullAddress) {
    let url = `https://maps.googleapis.com/maps/api/geocode/json?address=${fullAddress}&sensor=false&key=${this.googleAPIKey}`
    return this._http.get(url);
  }
  public validateAddress(fullAddress) {
    let url = `${apiServer}/api/helpers/address/validate?input=${encodeURIComponent(fullAddress)}&sid=${this.sessionId}`;
    //console.log(url);
    return this._http.post(url, {});
  }

  public validateJobAddress(jobID:number) {
    let url = `${apiServer}/api/helpers/address/validate?id=${jobID}&sid=${this.sessionId}`;
    //console.log(url);
    return this._http.post(url, {});
  }


  openContractorBillingForm(jobID: number) {
    let server = this.pdfServer;
    let sid = this.sessionId;
    let url = `${server}/api/ContractorBillingForm?id=${jobID}&template=ContractorBilling&sid=${sid}`
    let w = window.open(url);
    w.focus();
  }


  public getDefaultGoolgeFolders() {
    let df = this.googleWorkingFolders;
    if (df.length === 0) {
      let cmd = new Command();
      cmd.procedure = 'cmdGetGoogleFolderDefaults';
      return this.command(cmd)
        .pipe(
          map((folders: any) => {
            //console.log('googleWorkingFolders');
            this.googleWorkingFolders = folders;
            return folders;
          })
        );
    } else {
      return of<any>(df);
    }
  }

  public getGoogleStorageSignedURL(gsURI, minutes = 5): Observable<any> {
    let url = `${this.apiServer}/api/googlestorage?sid=${this.sessionId}`;
    return this._http.post(url, { file: gsURI, minutes: minutes });
  }

  public triggerJobPackageImport(folderId: string, importType: string = 'jobPackage') {
    var url;
    if (importType == 'PGEJobPackage') {
      url = `${this.apiServer}/api/AnalyzeJob?ParentFolderID=${folderId}&ImportType=PGEJobPackage&sid=${this.sessionId}`;
    } else {
      url = `${this.apiServer}/api/DistributeJobPackage/${folderId}?sid=${this.sessionId}`;
    }
    return this._http.get(url);
  }

  public bidEntry(): Observable<any> {
    let url = this.apiServer + `/api/bidentry?sid=${this.sessionId}`;
    var today = new Date();
    today.setHours(0, 0, 0, 0);

    let o = { bidRequestDate: today }
    //console.log(url);
    return this._http.post(url, o);
  }

  public analyzeJobPDF(googleDriveFileID) {
    let url = `${this.apiServer}/api/JobAI?fileId=${googleDriveFileID}&ImportType=JobAnalyze&sid=${this.sessionId}`;
    return this._http.get(url);
  }

  public analyzeNewJobPDF(googleDriveFileID) {
    let url = `${this.apiServer}/api/AnalyzeJob/${googleDriveFileID}?sid=${this.sessionId}`;
    return this._http.get(url);
  }


  public preloadReport(o: any) {
    let url = this.apiServer + `/api/jobpreload?sid=${this.sessionId}`;
    //console.log(o)
    //console.log(url);
    return this._http.post(url, o);
  }

  public getJobs(): Observable<any> {
    let url = this.apiServer + "/api/Job?sid=" + this.sessionId;
    return this._http.get(url);
  }
  public getJob(id): Observable<any> {
    let url = this.apiServer + "/api/Job/" + id + "?sid=" + this.sessionId;
    return this._http.get(url);
  }

  getJobImages(id: number): Observable<any> {
    let cmd = new Command();
    cmd.procedure = 'cmdJobPhotos'
    cmd.addParameter('JobID', id);
    let pics$ = this.command(cmd);

    return pics$;
  }

  //THIS IS THE WAY TO SAVE ALL JOB PHOTOS
  uploadPhoto(photo: any): Observable<any> {
    photo.sessionId = this.sessionId;
    var url = this.apiServer + '/api/Image?sid=' + this.sessionId;
    let retval$ = this._http.post(url, photo);
    return retval$
  }

  deletePhoto(referenceCode: string, jobID: number): Observable<any> {
    var url = this.apiServer + '/api/Image?referenceCode=' + referenceCode + '&jobID=' + jobID + '&sid=' + this.sessionId;
    let retval$ = this._http.delete(url);
    return retval$
  }

  async getJobStatusOptions(statusCode = 'JOB') {
    let cmd = new Command();
    cmd.procedure = "cmdGetJobStatus";
    return firstValueFrom(
      this.command(cmd).pipe(
        map((retval: any) => retval.filter(row => row.statusCode === statusCode))
      )
    );
  }


  deleteJob(id) {
    let cmd = new Command();
    cmd.procedure = "cmdFieldUpdate";
    cmd.addParameter("Table", "Job");
    cmd.addParameter("Field", "Status");
    cmd.addParameter("Value", "DELETED");
    cmd.addParameter("Key", "JobID");
    cmd.addParameter("ID", id);
    return this.command(cmd).pipe(
      map(async (retval) => {
        await this.idb.delete('jobs', id).then(resp => console.log('deleted from idb', resp))
        return retval;
      })
    )
  }


  exportTimeSheet(ts: any) {
    let endPoint = `${this.apiServer}/api/timesheet/export?sid=${this.sessionId}&id=${ts.timeSheetID}`;
    return this._http.get(endPoint);

  }
  createJobRecord(job, options: { rfp: boolean, jobPackage: string }) {

    let endPoint = `${this.apiServer}/api/job/add-record?sid=${this.sessionId}&rfp=${options.rfp}&jobPackage=${options.jobPackage}`;
    return this._http.post(endPoint, job);
  }

  setJobFolders(jobID) {
    let endPoint = `${this.apiServer}/api/job/${jobID}/add-folders?sid=${this.sessionId}`;
    return this._http.post(endPoint, null);
  }

  setJobFoldersWithLocations(jobID, locations: string) {
    let sanitizedLocations = encodeURIComponent(locations);
    let endPoint = `${this.apiServer}/api/job/${jobID}/add-folders?locations=${sanitizedLocations}&sid=${this.sessionId}`;
    return this._http.post(endPoint, null);
  }

  // FOLDER_TEMPLATE flag: template-driven folder build. With async=true the server
  // returns { success, crewFolderID, bidFolderID, ... } as soon as the root folders
  // exist and finishes subfolders/actions/locations in the background.
  buildJobFolderTemplate(jobID: number) {
    let endPoint = `${this.apiServer}/api/FolderTemplate/Build/${jobID}?sid=${this.sessionId}&async=true`;
    return this._http.post(endPoint, null);
  }

  public newJobSites(jobID: number, locations: Site[]) {

    let url = this.apiServer + "/api/SiteBuilder/" + jobID + "?sid=" + this.sessionId;
    return this._http.post(url, locations);
  }
  public getJobSites(jobID: number): Observable<any> {
    let url = this.apiServer + "/api/SiteBuilder/" + jobID + "?sid=" + this.sessionId;
    return this._http.get(url);
  }
  public getSites(): Observable<any> {
    let url = this.apiServer + "/api/Site?siteid=0&sid=" + this.sessionId;
    return this._http.get(url);
  }
  public getSite(id): Observable<any> {
    let url = this.apiServer + "/api/Site?siteid=" + id + "&sid=" + this.sessionId;
    return this._http.get(url);
  }
  public addNewSite(site) {
    let url = this.apiServer + "/api/Site?sid=" + this.sessionId;
    return this._http.post(url, site)
      .pipe(map((res: any) => {

        return res[0];
      }));
  }
  public saveSite(site: Site) {
    let url = this.apiServer + "/api/Site?sid=" + this.sessionId;
    return this._http.put(url, site)
      .pipe(map((res: any) => this.returnFirstItem(res)));
  }

  public addSiteToSchedule(obj: AssignmentSchedule): Observable<any> {
    let url = this.apiServer + "/api/Schedule?sid=" + this.sessionId;
    return this._http.post(url, obj)
      .pipe(map((res: any) => this.returnFirstItem(res)));
  }
  public addNewReport(obj) {
    let url = this.apiServer + "/api/Report?sid=" + this.sessionId;
    return this._http.post(url, obj)
      .pipe(map((res: any) => this.returnFirstItem(res)));
  }
  public getReports(): Observable<any> {
    let url = this.apiServer + "/api/Report?reportid=0&sid=" + this.sessionId;
    return this._http.get(url);
  }

  public getReportById(reportId) {
    let url = this.apiServer + "/api/Report?reportid=" + reportId + "&sid=" + this.sessionId;
    return this._http.get(url);
  }

  public saveReport(report: Report): Observable<any> {
    if (report.reportID) {

      let url = this.apiServer + "/api/Report?reportid=" + report.reportID + "&sid=" + this.sessionId;
      return this._http.put(url, report);

    } else {

      let url = this.apiServer + "/api/Report?sid=" + + this.sessionId;
      return this._http.post(url, report);

    }
  }

  public getReportTemplates(): Observable<any> {
    let url = this.apiServer + "/api/ReportTemplate?ReportTemplateID=0&sid=" + this.sessionId;
    return this._http.get(url);
  }
  public getReportTemplateById(id): Observable<any> {
    let url = this.apiServer + "/api/ReportTemplate?ReportTemplateID=" + id + "&sid=" + this.sessionId;
    return this._http.get(url);
  }
  public saveReportTemplate(template: ReportTemplate): Observable<any> {
    let cmd = new Command();
    cmd.procedure = 'cmdReportTemplateSave';
    cmd.addParameter('ReportTemplateID', template.reportTemplateID);
    cmd.addParameter('reportName', template.reportName);
    cmd.addParameter('description', template.description || '');
    cmd.addParameter('templateFile', template.templateFile || '');
    cmd.addParameter('reportTemplateName', template.reportTemplateName || '');
    cmd.addParameter('googleStorageUrl', template.googleStorageUrl || '');
    cmd.addParameter('selfSend', template.selfSend || '');
    cmd.addParameter('impersonateUser', template.impersonateUser || '');
    cmd.addParameter('distributionTo', template.distributionTo || '');
    cmd.addParameter('distributionCC', template.distributionCC || '');
    cmd.addParameter('distributionFrom', template.distributionFrom || '');
    cmd.addParameter('status', template.status || 'Active');
    return this.command(cmd);
  }

  public uploadReportTemplateFile(templateID: number, data: any): Observable<any> {
    // if (templateID > 0) {
    let headers = new HttpHeaders();
    /** In Angular 5, including the header Content-Type can invalidate your request */
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');

    let url = this.apiServer + "/api/ReportTemplate/Upload/" + templateID + "/?sid=" + this.sessionId;
    return this._http.post(url, data, { "headers": headers });
  }

  public uploadDashboardImg(dashboardItemID: number, data: any): Observable<any> {
    // if (templateID > 0) {
    let headers = new HttpHeaders();
    /** In Angular 5, including the header Content-Type can invalidate your request */
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');

    let url = this.apiServer + "/api/uploads/dashboardimage/" + dashboardItemID + "/?sid=" + this.sessionId;
    return this._http.post(url, data, { "headers": headers });
  }


  saveReportTemplateItem(templateID, templateItem: Question): Observable<Question> {
    if (templateItem.questionID) {

      let url = this.apiServer + "/api/ReportTemplate/" + templateID + "/Question/" + templateItem.questionID + "?sid=" + this.sessionId;
      return this._http.put(url, templateItem)
        .pipe(map((res: any) => res));

    } else {
      let url = this.apiServer + "/api/ReportTemplate/" + templateID + "/Question?sid=" + this.sessionId;
      return this._http.post(url, templateItem)
        .pipe(map((res: any) => res));
    }
  }

  public getQuestionById(questionId) {
    let url = this.apiServer + "/api/Question?sid=" + this.sessionId + "&questionid=" + questionId;
    return this._http.get(url)
      .pipe(map((res: any) => {

        return res[0];
      }));
  }

  public saveQuestion(obj: Question) {
    let url = this.apiServer + "/api/Question?sid=" + this.sessionId;
    if (obj.questionID) {
      return this._http.put(url, obj);
    } else {
      return this._http.post(url, obj);
    }
  }

  public getReportItemByID(reportItemID): Observable<ReportItem> {
    let url = this.apiServer + '/api/ReportItem?sid=' + this.sessionId + '&ReportItemID=' + reportItemID;
    return this._http.get(url)
      .pipe(map((res: any) => {

        return res[0];
      }));
  }


  public getReportItemNotesAndPhotosByID(reportItemID): Observable<any> {
    let url = this.apiServer + '/api/ReportItemNotesAndPhotos?id=' + reportItemID + '&sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => {

        return res;
      }));
  }

  public saveReportItem(obj: ReportItem) {
    let url = this.apiServer + "/api/ReportItem?sid=" + this.sessionId;
    if (obj.reportItemID) {
      return this._http.put(url, obj);
    } else {
      return this._http.post(url, obj);
    }

  }

  public updateReportItemResponse(obj: ReportItem) {
    let url = this.apiServer + "/api/SiteReviewResponse?sid=" + this.sessionId;
    if (obj.reportItemID) {
      return this._http.post(url, obj);
    } else {
      return this._http.post(url, obj);
    }

  }
  public getNotesByReport({ reportID, reportItemID }): Observable<Note[]> {
    if (this.selectedReport && this.selectedReport.reportID === reportID) {
      return of(
        this.filterNotes(this.selectedReport.notes, { reportItemID })
      );
    } else {
      return this.getReportById(reportID).pipe(map((res: Report) => {
        return this.filterNotes(res.notes, { reportItemID });
      }));
    }
  }

  public getPhotosByReport({ reportID, reportItemID }): Observable<any[]> {
    if (this.selectedReport && this.selectedReport.reportID === reportID) {
      return of(
        this.filterPhotos(this.selectedReport.photos, { reportItemID })
      );
    } else {
      return this.getReportById(reportID).pipe(map((res: Report) => {
        return this.filterPhotos(res.notes, { reportItemID });
      }));
    }
  }

  public getQuestionsByReport(reportID): Observable<any[]> {
    if (this.selectedReport) {
        //console.log('if')
      return of(this.selectedReport.reportQuestions);
    } else {
        //console.log('else', reportID)
      return this.getReportById(reportID)
        .pipe(map((res: any) => {
          return res.reportQuestions;
        }))
    }
  }

  public getAssignedSitesInTract(obj: any): Observable<any> {
    let url = this.apiServer + "/api/SitesAssignedInTract?sid=" + this.sessionId;
    return this._http.post(url, obj);
  }

  public getSitesByTract(id: number): Observable<any> {
    let url = this.apiServer + "/api/SiteInTract/" + id + '?sid=' + this.sessionId;
    return this._http.get(url);
  }



  public getSitesByPointSet(pointSet: string, tractID): Observable<any> {
    let obj: any = {};
    obj.pointSet = pointSet;
    obj.tractID = tractID;
    let url = this.apiServer + "/api/SiteInTract?sid=" + this.sessionId;
    return this._http.post(url, obj);
  }

  public savePolygon(polygon: Polygon) {
    //console.log(polygon)
    return polygon;
  }

  public getPolygon1(id: number): Observable<any> {
    var cmd = {
      "procedure": "getPolygon",
      "parameters": ["@PolygonID=" + id.toString(), "@SessionID='" + this.sessionId + "'"]

    }
    return this.command(cmd);
  }

  /*  ASSIGNMENT TEAMS */
  saveAssignmentTeam(t: AssignmentTeam) {
    let url = this.apiServer + "/api/AssignmentTeam?sid=" + this.sessionId;
    if (t.assignmentTeamID) {
      return this._http.put(url, t);
    } else {
      return this._http.post(url, t);
    }
  }

  getAssignmentTeam(id: number): Observable<AssignmentTeam[]> {
    let url = this.apiServer + "/api/AssignmentTeam/" + id + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));

  }
  getAssignmentTeamMembers(id): Observable<any> {
    let criteria = {
      "AssignmentTeamMemberID": 1,
      "AssignmentTeamID": 0,
      "InternalContactsIDInput": 0
    }
    let url = `${this.apiServer}/api/AssignmentTeamMember?AssignmentTeamMemberID=0&AssignmentTeamID=${id}&InternalContactsIDInput=0&sid=${this.sessionId}`;
    return this._http.get(url);
  }

  addAssignmentTeamMember(obj: any) {
    let url = this.apiServer + "/api/AssignmentTeamMember?sid=" + this.sessionId;
    return this._http.post(url, obj);
  }

  saveAssignmentTeamMember(obj: any) {
    let url = this.apiServer + "/api/AssignmentTeamMember?sid=" + this.sessionId;
    return this._http.put(url, obj);
  }

  getAssignmentTeamPermissions(teamId: number) {
    var cmd = new Command();
    cmd.procedure = 'cmdGetTeamPermissions';
    cmd.addParameter('AssignmentTeamID', teamId);
    return this.command(cmd).pipe(map((res: any) => res));
  }

  addAssignmentTeamPermission(teamId: number, url: string) {
    var cmd = new Command();
    cmd.procedure = 'cmdAddTeamPermission';
    cmd.addParameter('AssignmentTeamID', teamId);
    cmd.addParameter('GoogleURL', url);
    return this.command(cmd)
  }

  removeAssignmentTeamPermission(Id: number) {
    var cmd = new Command();
    cmd.procedure = 'cmdDeleteTeamPermission';
    cmd.addParameter('AssignmentTeamPermissionID', Id);
    return this.command(cmd)
  }

  newJobPackage(jobID: number) {
    let url = this.apiServer + "/api/jobs/" + jobID + "/submissions/new?sid=" + this.sessionId;
    return this._http.post(url, {});
  }
  autoCreateSubmission(jobID: number, GoogleFileID: string): Observable<any> {
    let url = this.apiServer + "/api/jobs/" + jobID + "/submissions/auto?sid=" + this.sessionId;
    let data = { jobID: jobID, googleFileID: GoogleFileID }
    return this._http.post(url, data);
  }
  submitJobPackage(jobID: number, submissionID: number, email: { to: string, subject: string, message: string }) {
    let url = this.apiServer + "/api/jobs/" + jobID + "/submissions/" + submissionID + "?sid=" + this.sessionId;

    return this._http.put(url, email)
      .pipe(map((res: any) => res));
  }
  infractionOptions() {
    var cmd = new Command();
    cmd.procedure = 'cmdInfractionOptions';
    return this.command(cmd)
  }

  submitJobPackageByFTP(obj: any, host: any) {
    let api = '/api/sftp'
    let submission = {
      submissionID: obj.submissionID,
      remotePath: '',
      hostID: host.hostID
    }
    let url = this.apiServer + api + '?sid=' + this.sessionId;
    return this._http.post(url, submission);
  }

  buildJobSiteFolders(jobID: number, locationNumber: number = 0) {

    let url = `${this.apiServer}/api/GoogleDrive/build-site-folders?jobid=${jobID}&locationNumber=${locationNumber}&sid=${this.sessionId}`;
    return this._http.post(url, {});
  }

  googleFolderList(folderId: string) {
    let url = this.apiServer + '/api/GoogleDrive/Folder/' + folderId + '?sid=' + this.sessionId;
    return this._http.get(url).pipe(map((res: any) => res));
  }

  uploadJobSubmissionFileFromGoogleDrive(jobID: number, submissionID: number, googleFileUrls: string[]) {
    let url = `${this.apiServer}/api/jobs/${jobID}/submissions/${submissionID}/driveupload?sid=${this.sessionId}`;
    return this._http.post(url, googleFileUrls);
  }
  makeGoogleFolder(folderObject: GoogleFolderCommandSet): Observable<any> {
    let url = this.apiServer + "/api/GoogleDrive/Folders?sid=" + this.sessionId;
    return this._http.post(url, folderObject);
  }
  renameGoogleFolder(folderId: string, name: string): Observable<any> {
    let url = `${this.apiServer}/api/GoogleDrive/Folders/${folderId}?name=${name}&sid=${this.sessionId}`;
    return this._http.put(url, {});
  }
  ResetJobFolderLocations(jobId: number): Observable<any> {
    let url = `${this.apiServer}/api/drivefolder/job/reset/?sid=${this.sessionId}&jobId=${jobId}`;
    return this._http.get(url);
  }
  copyTemplateFile(templateCode, targetFolderID): Observable<any> {
    let url = `${this.apiServer}/api/filecopy?templateName=${templateCode}&targetFolder=${targetFolderID}&sid=${this.sessionId}`;
    return this._http.post(url, {});
  }

  resetJobFolderShortCuts(bidFolderID: string): Observable<any> {
    let url = `${this.apiServer}/api/GoogleDrive/FileFolder/${bidFolderID}/shortcuts?command=rebuild&sid=${this.sessionId}`
    return this._http.post(url, null);
  }
  resetJobFolderNames(jobId: number): Observable<any> {
    let url = this.apiServer + "/api/GoogleDrive/ResetJobFolderNames/" + jobId + "?sid=" + this.sessionId;
    return this._http.get(url);
  }
  getJobTypes() {
    var cmd = new Command();
    cmd.procedure = "cmdGetJobTypes";
    let url: string = this.apiServer + '/api/cmd?tracker=' + cmd.procedure + '&sid=' + this.sessionId;
    return this._http.post(url, cmd)
      .pipe(map((res: any) => res));
  }

  getUsers(id?: number): Observable<AssignmentTeam[]> {

    let url = this.apiServer + "/api/user?sid=" + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }
  public getUser(id: number): Observable<any> {
    let url: string = this.apiServer + '/api/User/' + id + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((response: any) => <any>response));
  }
  public getUserList(): Observable<any> {
    let url: string = this.apiServer + '/api/User' + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => {
        let users: any[] = res;
        return users.map(user => {
          user.FullName = user.FirstName + ' ' + user.LastName;
          return user;
        });
      }));
  }




  public saveUser(user) {
    if (user.internalContactsID) {
      let url: string = this.apiServer + '/api/User/' + user.internalContactsID + '?sid=' + this.sessionId;
      return this._http.put(url, user)
        .pipe(map((response: any) => <any>response));
    } else {
      let url: string = this.apiServer + '/api/User?sid=' + this.sessionId;
      return this._http.post(url, user)
        .pipe(map((response: any) => <any>response));
    }
  }


  public updateUserCalendar(calendarList: any[]) {
    let url: string = this.apiServer + '/api/user/calendar' + '?sid=' + this.sessionId;
    return this._http.post(url, calendarList)
      .pipe(map((response: any) => <any>response));
  }

  saveAssignment(a: Assignment): Observable<Assignment> {
    let siteIDArray: string = a.siteIDArray;
    let obj: any = {};
    obj.siteIDArray = siteIDArray;
    obj.assignmentName = a.assignmentName;
    obj.assignmentTeamID = a.assignmentTeamID;
    obj.tractID = a.tractID;
    obj.effectiveDate = a.effectiveDate;


    let url = this.apiServer + "/api/SiteAssignment?sid=" + this.sessionId;
    if (a.assignmentID) {
      return this._http.put(url, obj)
        .pipe(map((res: any) => res));
    } else {
      return this._http.post(url, obj)
        .pipe(map((res: any) => res));
    }
  }
  getAssignmentList() {
    let url = this.apiServer + "/api/Assignment?sid=" + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }
  getAssignment(id: number): Observable<Assignment> {
    let url = this.apiServer + "/api/SiteAssignment/" + id + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }
  getTractStatistics(id): Observable<Tract> {
    let url = this.apiServer + "/api/SiteInTractStats/" + id + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }
  getTract(id): Observable<Tract> {

    let url = this.apiServer + "/api/Tract/" + id + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }

  getTracts(): Observable<Tract[]> {
    let url = this.apiServer + "/api/Tract?sid=" + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }

  saveTract(t: Tract) {
    let url = this.apiServer + "/api/Tract?sid=" + this.sessionId;
    if (t.tractID) {
      return this._http.put(url, t)
        .pipe(map((res: any) => res));
    } else {
      return this._http.post(url, t)
        .pipe(map((res: any) => res));
    }
  }

  getCustomers(): Observable<any> {
    let cmd = new Command();
    cmd.procedure = 'cmdCustomers';
    return this.command(cmd)
  }

  getReportList(reportName: string): Observable<any> {
    let cmd = new Command();
    cmd.procedure = 'getReport';
    cmd.addParameter("ReportName", reportName)
    return this.command(cmd);
  }

  async getCustomersAsync() {
    let cmd = new Command();
    cmd.procedure = 'cmdCustomers';
    return firstValueFrom(this.command(cmd));
  }

  async getCustomerOptionsByDistrictAsync(districtID: number) {
    let cmd = new Command();
    cmd.procedure = 'cmdCustomerOptionsByDistrict';
    cmd.addParameter('DistrictID', districtID)
    return firstValueFrom(this.command(cmd));
  }

  slacker(msg: string, error: string = 'none') {
    let url = this.apiServer + '/api/logger/slack';
    let loc = window.location.href;
    let session = JSON.parse(localStorage.getItem("session"))
    let user = session.idTokenPayload
    var message = {
      "text": "Dash Logger",
      "blocks": [
        {
          "type": "section",
          "text": {
            "type": "mrkdwn",
            "text": msg
          }
        },
        {
          "type": "section",
          "text": {
            "type": "mrkdwn",
            "text": "<" + loc + "|" + loc + ">"
          }
        },
        {
          "type": "section",
          "fields": [
            {
              "type": "mrkdwn",
              "text": "*Details*\n" + error
            }
          ]
        },
        {
          "type": "section",
          "fields": [
            {
              "type": "mrkdwn",
              "text": "*User*\n" + JSON.stringify(user)
            }
          ]
        }
      ]
    }
    //console.log(message);
    this._http.post(url, message).subscribe();
  }

  addCustomer(c): Observable<any> {
    let url = this.apiServer + "/api/Customer?sid=" + this.sessionId;
    return this._http.post(url, c)
      .pipe(map((res: any) => res));
  }

  getCustomer(customerID): Observable<any> {
    let cmd = new Command();
    cmd.procedure = 'cmdCustomers';
    cmd.addParameter('CustomerID', customerID)
    return this.command(cmd)
  }

  getMaterials(id: number = 0) {
    let cmd = new Command();
    cmd.procedure = 'cmdMaterial';
    cmd.addParameter('MaterialID', id);
    cmd.addParameter('Code', '');
    cmd.addParameter('Name', '');
    cmd.addParameter('Status', '');
    cmd.addParameter('Action', 'SELECT');
    return this.command(cmd)
  }

  getBids() {
    let url: string = this.apiServer + '/api/bidentry?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }

  getBid(id: number) {
    let cmd = new Command();
    cmd.procedure = "cmdBid";
    cmd.addParameter("BidID", id);
    return this.command(cmd)
  }

  getBidEntry(jobID: number) {
    let url: string = this.apiServer + '/api/bidentry/' + jobID + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }

  saveBid(bid: any) {
    let url: string = this.apiServer + '/api/bid?sid=' + this.sessionId;
    return this._http.post(url, bid)
      .pipe(map((res: any) => res));
  }

  saveBidEntry(bid: any) {
    //console.log("saveBidEntry", bid)
    let url: string = this.apiServer + '/api/bidentry?sid=' + this.sessionId;
    return this._http.put(url, bid)
      .pipe(
        map((response: Response) => <any>response)
      )
  }

  addLocation(job) {
    let url: string = this.apiServer + '/api/bidentry/?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }

  addLocationFolder(jobID: number) {
    let url: string = this.apiServer + '/api/joblocation/' + jobID + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }

  command(commandObject: any): Observable<any> {
    let url: string = this.apiServer + '/api/cmd?tracker=' + commandObject.procedure + '&sid=' + this.sessionId;
    return this._http.post(url, commandObject)
      .pipe(map((res: any) => res));
  }

  commandList(commandObject: any): Observable<any> {
    let url: string = this.apiServer + '/api/cmd/list?tracker=' + commandObject.procedure + '&sid=' + this.sessionId;
    return this._http.post(url, commandObject)
      .pipe(map((res: any) => res));
  }

  // commandBatch(commandObject: any): Observable<any> {
  //   let url: string = this.apiServer + '/api/cmd/batch?tracker=' + commandObject.procedure + '&sid=' + this.sessionId;
  //   return this._http.post(url, commandObject)
  //     .pipe(map((res: any) => res));
  // }

  commandBatch(commands: any[]): Observable<any> {
    let url: string = this.apiServer + '/api/cmd/batch?sid=' + this.sessionId;
    return this._http.post(url, commands)
      .pipe(map((res: any) => res));
  }

  private filterNotes(noteList: Note[], options: Note): Note[] {
    options = options || {};
    options.status = options.status || "Active";

    if (options.reportItemID) {
      noteList = noteList.filter((n: Note) => {
        return (
          n.status === options.status && n.reportItemID === options.reportItemID
        );
      });
    } else {
      noteList = noteList.filter(n => {
        return n.status === options.status && !n.reportItemID;
      });
    }
    return noteList;
  }

  public filterPhotos(photos: any[], options: any = {}): any[] {
    options = options || {};
    options.status = options.status || "Active";
    if (options.reportItemID) {
      photos = photos.filter(p => {
        return (
          p.status === options.status && p.reportItemID === options.reportItemID
        );
      });
    } else {
      photos = photos.filter(p => {
        return p.status === options.status && !p.reportItemID;
      });
    }

    return photos;
  }

  // CCSC and  DOCUMENT
  getDocFieldsets(reportID, docID): Observable<any> {
    let url = this.apiServer + "/api/report/" + reportID + "/document/" + docID + "?sid=" + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }
  saveDocFieldsets(reportID, docID, fieldSets: any[]): Observable<any> {
    let url = this.apiServer + "/api/report/" + reportID + "/document/" + docID + "?sid=" + this.sessionId;
    return this._http.post(url, fieldSets)
      .pipe(map((res: any) => res));
  }

  returnFirstItem(items) {
    items = items;
    return items[0] || {};
  }

  async getOptions(optionName: string) {
    let cmd = new Command();
    cmd.procedure = 'cmdOptionList';
    cmd.addParameter('OptionName', optionName);
    return firstValueFrom(this.command(cmd));
  }

  loadJobEventByEventID(eventID: number): Observable<JobEventListItem> {
    let url = `${this.apiServer}/api/job/event?sid=${this.sessionId}&eventid=${eventID}`;
    let event = <Observable<JobEventListItem>>this._http.get(url).pipe(
      tap((obj: any) => {
        //console.log('tap event type', obj.eventType)
      }
      ),
      map((obj: any) => {
        //console.log('map event type', obj.eventType)
        return obj;
      }),
      tap((obj: any) => { console.log('tap event type', obj.eventType) })
    );
    return event;
  }
  
  // async loadJobEvents(jobID: number): Promise<JobEventListItem[]> {
  //   alert('this is the incorrect procedure for loading events');
  //   return null;
  // }

  async deleteJobEvent(eventID: number): Promise<any> {
    let url = `${this.apiServer}/api/job/events/${eventID}/delete?sid=${this.sessionId}`;
    return firstValueFrom(this._http.post(url, null));
  }

  loadJobEventsOb(jobID: number): Observable<JobEventListItem[]> {
    let url = `${this.apiServer}/api/job/${jobID}/events/?sid=${this.sessionId}&id=${jobID}`;
    let events = this._http.get(url)
      .pipe(
        map((retval: JobEventListItem[]) => {
          return retval;
        })
      )
    return events;
  }
  serverSideEventDescription(fields: any[], jobID: number) {
    let url = `${this.apiServer}/api/job/${jobID}/event/describe`;
    return this._http.post(url, fields);
  }
  serverSideEventDescriptionNew(fields: any[], jobID: number) {
    let url = `${this.apiServer}/api/job/${jobID}/event/describe`;
    const body = { fields };
    return this._http.post<string>(url, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  getRowData(jobID) {
    let cmd = new Command();
    cmd.procedure = 'cmdJobEvent';
    cmd.addParameter("JobID", jobID);
    return this.command(cmd);
  }


  createCalendarEvent(jobID: number, event: CalendarEvent) {
    let url = `${this.apiServer}/api/job/${jobID}/Event/?eventType=${event.eventType}&sid=${this.sessionId}&startDateColumn=${event.startDateColumn}&endDateColumn=${event.endDateColumn}`;
    return this._http.post(url, event)
      .pipe(map((res: any) => res));
  }

  async cancelCalendarEvent(jobID: number, eventID: number, reason: string = '') {
    let url = `${this.apiServer}/api/job/${jobID}/events/${eventID}/cancel?reason=${reason}&sid=${this.sessionId}`;
    return firstValueFrom(
      this._http.put(url, {}).pipe(
        map((res: any) => res)
      )
    );
  }

  getCalendarListWithJobID(jobID) {
    let url: string = this.apiServer + `/api/Calendar?jobid=${jobID}&sid=` + this.sessionId;
    return this._http.get(url)
      .pipe(
        tap(obj => { console.log('original calendar download'); }),
        map((res: any) => res));
  }

  getCalendarList() {
    let url: string = this.apiServer + '/api/calendar/?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(
        tap(obj => { console.log('original calendar download'); }),
        map((res: any) => res));
  }

  getUserCalendarList(email: string) {
    let url: string = this.apiServer + '/api/calendar/userroles/' + email + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }


  getCalendar(id) {
    let url: string = this.apiServer + '/api/calendar/' + id + '?sid=' + this.sessionId;
    return this._http.get(url)
      .pipe(map((res: any) => res[0]));
  }
  updateCalendar(obj) {
    let url: string = this.apiServer + '/api/calendar/?sid=' + this.sessionId;
    return this._http.put(url, obj)
      .pipe(map((res: any) => res));
  }
  newCalendar(obj) {

    let url: string = this.apiServer + '/api/calendar/?sid=' + this.sessionId;
    return this._http.post(url, obj)
      .pipe(map((res: any) => res));
  }

  getOfficeAccess(internalContactsID: number) {
    let cmd = new Command();
    cmd.procedure = 'cmdOfficeAccess';
    cmd.addParameter("InternalContactsID", internalContactsID);
    return this.command(cmd);
  }

  importPackageHistory(importPackageID) {
    let cmd = new Command();
    cmd.procedure = 'cmdImportPackageHistory';
    cmd.addParameter('ImportPackageID', importPackageID);
    return this.command(cmd);
  }

  getDashboardData() {
    var cmd = new Command();
    cmd.procedure = "cmdGetDashboard";
    cmd.addParameter("DashboardID", 6);
    cmd.addParameter("DashboardType", "DASH");
    return this.command(cmd)
  }
  
  getUserDashboardData() {
    var cmd = new Command();
    cmd.procedure = "cmdDashboardDefault";
    return this.command(cmd)
  }

  timeSheetAdminReport(weekNum, foremanID, yearNumber) {
    let url: string = this.apiServer + '/api/timesheet/approval?sid=' + this.sessionId + '&weekNumber=' + weekNum + '&foremanID=' + foremanID + '&yearNumber=' + yearNumber;
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }

  timeSheetApproval(week) {
    let startDate = new Date(week.startDate).toLocaleDateString();
    let endDate = new Date(week.endDate).toLocaleDateString();
    return this.getFinancialReviewData(startDate, endDate);
  }

  timeSheetValidation(timeSheetID) {
    let url = this.apiServer + '/api/timesheet/' + timeSheetID + '/validation?sid=' + this.sessionId;
    return this._http.get(url);
  }

  processTimeSheetsToPenta(processedTSs) {
    let url = this.apiServer + '/api/pentarelay/process-batch?sid=' + this.sessionId;
    return this._http.post(url, processedTSs)
      .pipe(map((res: any) => res));
  }

  processUploadJobFile(file, url) {
    return this._http.post(url, file)
      .pipe(map((res: any) => res));
  }

  jobFileList(jobid, location = 'GD') {
    if (location === 'GD') {
      let url = `${this.apiServer}/api/GoogleDrive/JobFolders/${jobid}/list?sid=${this.sessionId}`
      return this._http.get(url)
        .pipe(map((res: any) => res));
    } else {
      let url = `${this.apiServer}/api/Archive/Job/${jobid}/List?sid=${this.sessionId}`
      return this._http.get(url)
        .pipe(map((res: any) => res));
    }
  }

  archiveJobFiles(jobid, deleteFromGD = true, setJobToArchived = false) {
    let url = `${this.apiServer}/api/Archive/Job/${jobid}/StoreFolder?deleteFromGD=${deleteFromGD}&setJobToArchived=${setJobToArchived}&sid=${this.sessionId}`
    return this._http.post(url, null)
      .pipe(map((res: any) => res));
  }

  restoreJobFiles(jobid, deleteFromGS = true, setJobToActive = false, rebuildDirectory = false, rebuildShortcuts = false) {
    let url = `${this.apiServer}/api/Archive/Job/${jobid}/Restore?deleteFromGS=${deleteFromGS}&setJobToActive=${setJobToActive}&rebuildDirectory=${rebuildDirectory}&rebuildShortcuts=${rebuildShortcuts}&sid=${this.sessionId}`
    return this._http.post(url, null)
      .pipe(map((res: any) => res));
  }


  analyzeJobData(jobDataID) {
    let url = `${this.apiServer}/api/AnalyzeJob/${jobDataID}?sid=${this.sessionId}`
    return this._http.post(url, null)
      .pipe(map((res: any) => res));
  }


  processFile(url) {
    return this._http.post(url, null)
      .pipe(map((res: any) => res));
  }

  //Envents methods
  on(eventType: AppEventType): Observable<AppEvent<any>> {
    return this.eventBrocker.pipe(filter((event: any) => event.type === eventType));
  }

  dispatch<T>(event: AppEvent<T>): void {
    this.eventBrocker.next(event);
  }


  sendClientCommand(commandName, commandMessage) {
    let url = `${this.apiServer}/api/signal/broadcast?commandName=${commandName}&sid=${this.sessionId}`;
    return this._http.post(url, commandMessage)
  }


  getUSATicketsJobDetails(pMNumber) {
    let url = `https://appsca.undergroundservicealert.org/posr/searchtool/PositiveResponse/GetJobsListForTable?format=json&ticketNumber=%${pMNumber}%20`
    return this._http.get(url)
      .pipe(map((res: any) => res));
  }


  getChunk(beginDate, endDate) {
    let strBeginDate = this.toolbox.formatJsToSqlDate(beginDate);
    let estrEndDate = this.toolbox.formatJsToSqlDate(endDate);
    let url = `${this.apiServer}/api/jobmaster/chunk?begindate=${strBeginDate}&enddate=${estrEndDate}&sid=${this.sessionId}`;
    return this._http.post(url, {})
  }

  getJobsGoogleJson(version: number = 1): Observable<any> {
    let url = '';
    if (version) {
      url = `${this.apiServer}/api/jobmaster/cache-links?sid=${this.sessionId}&version=${version}`;
      return this._http.get(url).pipe(
        map((res: any[]) => res.map(item => item.gsURL)), // Extract gsURL from each object
        switchMap((urlArray: string[]) => {
          // Handle both single URL and array cases for backwards compatibility
          const urls = Array.isArray(urlArray) ? urlArray : [urlArray];

          // Create an array of HTTP requests
          const requests = urls.map(url =>
            this._http.get<any[]>(url).pipe(  // Type as array
              retry(3),
              catchError(this.handleError)
            )
          );

          // Execute all requests in parallel and combine the arrays
          return forkJoin(requests).pipe(
            map((results: any[][]) => {
              // Flatten all arrays into a single array
              return results.flat();
            })
          );
        })
      );
    } else {
      url = `${this.apiServer}/api/jobmaster/cache-link?sid=${this.sessionId}&key=sbqtwgyqvsrcbwcb6rf87b`;
      return this._http.get(url).pipe(
        map((res: any) => res.gsURL),
        switchMap((URLjsonFile: string) =>
          this._http.get(URLjsonFile).pipe(
            retry(3),
            catchError(this.handleError)
          )
        )
      );
    }



  }

  handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  getMostRecentWorkbookData(): Observable<any> {
    let url = `${this.apiServer}/api/pgeupload/get-latest?sid=${this.sessionId}`;
    return this._http.get(url);
  }

  downloadUpdatedWorkbook(workbookID) {
    let url = `${this.apiServer}/api/pgeupload/download-updated-workbook/${workbookID}?sid=${this.sessionId}`;
    return url;
  }


  getColumnHeadersList(): Observable<any> {
    let url = `${this.apiServer}/api/pgeupload/verify-headers?sid=${this.sessionId}`;
    return this._http.post(url, null).pipe(
      map((response: any) => response.pGEHeaderName || []),
      catchError(error => {
        console.error('Error fetching column headers:', error);
        return of([]);
      })
    );
  }

  uploadWorkbook(columnsInfo: any[], file: File, date: string): Observable<any> {
    let url = `${this.apiServer}/api/pgeupload/store?sid=${this.sessionId}`;
    const formData = new FormData();
    formData.append('file', file);
    const requestPayload = {
      columnsInfo: columnsInfo,
      originalFile: {
        name: file.name,
        size: file.size,
        type: file.type
      },
      date: date
    };
    formData.append('request', JSON.stringify(requestPayload));

    return this._http.post(url, formData).pipe(
      catchError(error => {
        console.error('Error uploading data:', error);
        throw error;
      })
    );
  }

  searchJobByOrderNotification(pmNumber, notificationNumber) {
    let cmd = new Command();
    cmd.procedure = "cmdJobDuplicateSearch";
    cmd.addParameter("jobNumber", pmNumber);
    cmd.addParameter("NotificationNumber", notificationNumber);
    return this.command(cmd).pipe(
      map((retval) => {
        //console.log('cmdJobDuplicateSearch ', retval)
        return retval;
      })
    );
  }

  getAddressSuggestions(input: string): Observable<any[]> {
    const url = `${this.apiServer}/api/helpers/address/suggest?input=${input}`;
    return this._http.get<any[]>(url);
  }

  getPlaceDetails(placeId: string): Observable<any> {
    return this._http.post<any>(`${this.apiServer}/api/helpers/address/place?id=${placeId}`, {});
  }

    // Get all infractions for a job
  getJobInfractions(jobId: number): Observable<JobInfraction[]> {
    return this._http.get<JobInfraction[]>(`${this.apiServer}/api/jobs/${jobId}/infractions?sid=${this.sessionId}`);
  }

  // Get all infractions (for the grid view)
  // Note: If there's no specific endpoint for all infractions across all jobs,
  // you may need to add one on the backend or adjust the grid to filter by job
  getAllInfractions(): Observable<JobInfraction[]> {
    return this._http.get<JobInfraction[]>(`${this.apiServer}/api/jobs/infractions?sid=${this.sessionId}`);
  }

  // Create new infraction
  createInfraction(jobId: number, infraction: JobInfraction): Observable<JobInfraction> {
    return this._http.post<JobInfraction>(`${this.apiServer}/api/jobs/${jobId}/infractions?sid=${this.sessionId}`, infraction);
  }

  // Update existing infraction
  updateInfraction(jobId: number, jobInfractionId: number, infraction: JobInfraction): Observable<JobInfraction> {
    return this._http.put<JobInfraction>(`${this.apiServer}/api/jobs/${jobId}/infractions/${jobInfractionId}?sid=${this.sessionId}`, infraction);
  }

  // Delete infraction
  deleteInfraction(jobId: number, jobInfractionId: number): Observable<any> {
    return this._http.delete(`${this.apiServer}/api/jobs/${jobId}/infractions/${jobInfractionId}?sid=${this.sessionId}`);
  }


  // Timesheets Reviews, filter data by user districts access
  filterDataByUserDistricts(data: any[]): any[] {
    if (!data || data.length === 0) {
      return [];
    }
  
    const curUserDataStr = localStorage.getItem('curUserData');
    const curUserData = JSON.parse(curUserDataStr);
    const officeAccess: any[] = curUserData.officeAccess || [];
  
    if (officeAccess.length === 0) {
      return [];
    }
  
    const allowedDistrictIds = new Set<number>();
  
    officeAccess.forEach(o => {
      if (o.hasAccess === 1 && o.districtID != null) {
        const districtId = Number(o.districtID);
        if (!isNaN(districtId)) {
          allowedDistrictIds.add(districtId);
        }
      }
    });
    if (allowedDistrictIds.size === 0) {
      return [];
    }
    return data.filter(row => {
      const rowDistrictId = Number(row?.districtID);
      return !isNaN(rowDistrictId) && allowedDistrictIds.has(rowDistrictId);
    });
  }


  wbExpandedSearch(searchObj: any): Observable<any> {
   let cmd = new Command();
    cmd.procedure = 'cmdWeeklyBillingView';
    cmd.addParameter('StartDate', new Date(searchObj.startDate).toISOString());
    cmd.addParameter('EndDate', new Date(searchObj.endDate).toISOString());
    cmd.addParameter('SearchType', searchObj.searchBy);
    cmd.addParameter('SearchValues', searchObj.input);
    
    return this.command(cmd).pipe(
      map((res: any) => {
        //console.log(res)
        let data = res;
        return data;
      })
    );
  }

  WBaddJobEarning(data: any): Observable<any> {
    return this._http.post(`${this.apiServer}/api/jobearnings?sid=${this.sessionId}`, data);
  }

  WBdeleteJobEarning(data: any): Observable<any> {
    return this._http.delete(`${this.apiServer}/api/jobearnings/${data.jobEarningID}?sid=${this.sessionId}`);
  }


  timeSheetValidationBulk(timeSheetIDs: number[]): Observable<any[]> {
    const url = `${this.apiServer}/api/timesheet/bulk-validator?sid=${this.sessionId}`;
    return this._http.post<any[]>(url, timeSheetIDs).pipe(
      map(res => res ?? [])
    );
  }



readonly locateTicketResult$ = this.locateTicketInput$.pipe(
  debounceTime(600),
  distinctUntilChanged(),
  switchMap((ticketNumber: string) => {
    if (!ticketNumber || !ticketNumber.trim()) {
      return of(null);
    }
    return this.getLocateTicketLink(ticketNumber.trim()).pipe(
      catchError((err) => {
        console.error('Error fetching locate ticket link', err);
        return of(null);
      })
    );
  })
);

pushLocateTicketNumber(ticketNumber: string) {
  this.locateTicketInput$.next(ticketNumber);
}

getLocateTicketLink(ticketNumber: string) {
  let url: string = `${this.apiServer}/api/locates/link?number=${encodeURIComponent(ticketNumber)}`;
  return this._http.get(url)
    .pipe(map((response: any) => <any>response));
}

getLeaveTypes() {
   let cmd = new Command();
    cmd.procedure = 'cmdGetLeaveTypes';
    
    return this.command(cmd).pipe(
      map((res: any) => {
        console.log('cmdGetLeaveTypes ', res)
        let data = res;
        return data;
      })
    );
  }

uploadTagFile(formData: FormData, tagID?: number): Observable<any> {
  const path = tagID
    ? `${this.apiServer}/api/tags/${tagID}/files?sid=${this.sessionId}`
    : `${this.apiServer}/api/tags/files?sid=${this.sessionId}`;

  return this._http.post(path, formData);
}

loadTagimage(tagID: any, uploadID: any): Observable<any> {
  return this._http.get(`${this.apiServer}/api/tags/${tagID}/img/${uploadID}?sid=${this.sessionId}`,{
    responseType: 'blob'
  });
}

}


//let's create an event system in this api
export enum AppEventType {
  ApiCalls = 'ALL_CALL',
  ApiUpdate = 'ALL_UPDATES',
}

export class AppEvent<T> {
  constructor(
    public type: AppEventType,
    public entityName: string,
    public payload: T,
  ) { }
}
